/**
 * LiveKitScreenPane
 *
 * Renders a presenter's live screen-share / camera track inside the Screen
 * Viewer when a room service is backed by a live LiveKit session (rather than a
 * static `endpoint` proxied as JPEG/MJPG/HLS).
 *
 * This is the viewer half of the LiveKit screen-share migration (P1): the room
 * "Present" button now publishes a screen track to `session-{id}` via the
 * presenter page, and the Screen Viewer subscribes here. It deliberately reuses
 * the same `useLivekitToken` + `useTracks` pattern as the session-join viewer
 * (`ViewerVideoPane`) so token handling, anonymous-viewer access, and track
 * filtering stay consistent across the two surfaces.
 *
 * Accessibility: the caller passes the same CSS `filter` string (grayscale /
 * invert) and zoom transform that the JPEG/`<video>` renderers use, so the
 * presentation a11y controls keep working on the LiveKit pane with no special
 * casing — the flagship zoom/grayscale/invert feature is preserved.
 */

import {
	LiveKitRoom,
	RoomAudioRenderer,
	useConnectionState,
	useTracks,
} from '@livekit/components-react'
import { ConnectionState, Track } from 'livekit-client'
import { Loader2, Video } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLivekitToken } from '@/hooks/use-livekit-token'
import { EnableAudioButton } from './enable-audio-button'
import { PresenterScreenEmpty, PresenterScreenTrack } from './presenter-screen-track'

// Connected-but-nothing-published grace period before the pane suggests the
// presenter is offline. Long enough to ride out a presenter restarting their
// share or briefly dropping; short enough that a viewer isn't left staring at
// "no video yet" with no explanation.
const PRESENTER_OFFLINE_TIMEOUT_MS = 30_000

interface LiveKitScreenPaneProps {
	/** LiveSession id (cuid) whose LiveKit room we subscribe to. */
	sessionId: string
	/** CSS filter string (grayscale/invert) applied to the video, from viewer settings. */
	filterStyle: string
	/**
	 * MP-187: reports the presenter's display name (LiveKit participant name,
	 * minted server-side into the token) so the host viewer can show who is
	 * presenting. Called with null while no remote presenter is publishing.
	 */
	onPresenterName?: (name: string | null) => void
	/**
	 * Reports fatal LiveKit room errors (connect failure, signal loss…) so the
	 * parent Screen Viewer can show its full-area error state instead of
	 * leaving the viewer on an infinite "waiting" pane. Optional — without it
	 * the pane falls back to the old console-only behavior.
	 */
	onFatalError?: (message: string) => void
	/**
	 * Reports the LiveKit room connection so the parent header can show a
	 * truthful status. Without it the header read the JPG-polling
	 * `isConnected`, which is always false for a share — the permanent
	 * "Nepřipojeno" MU reported (5. 8.).
	 */
	onConnectionChange?: (connected: boolean) => void
}

export function LiveKitScreenPane({
	sessionId,
	filterStyle,
	onPresenterName,
	onFatalError,
	onConnectionChange,
}: LiveKitScreenPaneProps) {
	const { t } = useTranslation()
	const tokenResult = useLivekitToken(sessionId, { enabled: !!sessionId })

	if (tokenResult.status === 'loading' || tokenResult.status === 'idle') {
		return (
			<div className="h-full flex flex-col items-center justify-center gap-2 text-base-content/60">
				<Loader2 size={28} className="animate-spin" />
				<p className="text-sm">{t('viewer.screen.loading')}</p>
			</div>
		)
	}

	// 503 (LiveKit disabled) / 401 (not permitted) / other — surface nothing here;
	// the parent Screen Viewer owns the full-area error state. Returning null lets
	// the caller fall through to its existing "source unavailable" UI.
	if (tokenResult.status === 'error' || !tokenResult.token || !tokenResult.url) {
		return (
			<div className="h-full flex flex-col items-center justify-center gap-2 text-base-content/60">
				<Video size={28} className="opacity-60" />
				<p className="text-sm">{t('sessions.join.video.waitingDesc')}</p>
			</div>
		)
	}

	return (
		<LiveKitRoom
			token={tokenResult.token}
			serverUrl={tokenResult.url}
			connect={true}
			video={false}
			audio={false}
			className="relative h-full w-full"
			onError={(err) => {
				console.error('[LiveKit] screen-viewer room error', err)
				onFatalError?.(err.message)
			}}
		>
			<RoomAudioRenderer />
			<EnableAudioButton className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10" />
			<LiveKitScreenTrack
				filterStyle={filterStyle}
				onPresenterName={onPresenterName}
				onConnectionChange={onConnectionChange}
			/>
		</LiveKitRoom>
	)
}

function LiveKitScreenTrack({
	filterStyle,
	onPresenterName,
	onConnectionChange,
}: {
	filterStyle: string
	onPresenterName?: (name: string | null) => void
	onConnectionChange?: (connected: boolean) => void
}) {
	const { t } = useTranslation()
	const tracks = useTracks([Track.Source.ScreenShare, Track.Source.Camera], {
		onlySubscribed: true,
	})

	// Only remote presenter publishes — never our own loopback. Prefer a
	// screen-share track over a camera track when the presenter shares both.
	const remoteTracks = useMemo(() => tracks.filter((track) => !track.participant.isLocal), [tracks])
	const primary = useMemo(
		() =>
			remoteTracks.find((trackRef) => trackRef.source === Track.Source.ScreenShare) ??
			remoteTracks[0],
		[remoteTracks],
	)

	// MP-187: surface who is presenting to the host viewer's header.
	const presenterName = primary?.participant.name || null
	useEffect(() => {
		onPresenterName?.(presenterName)
	}, [presenterName, onPresenterName])

	// Presenter-offline hint: connected to the room but nothing published for
	// 30s straight (presenter never arrived, or their last track detached).
	// Deliberately a calm inline hint — NOT the fatal `onFatalError` path —
	// because the presenter may simply be between shares; the `useTracks`
	// subscription stays live, so a (re)published track clears the hint and
	// resumes video with no action from the viewer.
	const connectionState = useConnectionState()
	// Mirror the room connection up to the parent header (see prop docstring).
	// Cleanup reports false so a torn-down pane doesn't leave a stale
	// "connected" badge behind.
	useEffect(() => {
		onConnectionChange?.(connectionState === ConnectionState.Connected)
		return () => onConnectionChange?.(false)
	}, [connectionState, onConnectionChange])
	const waitingForPresenter = connectionState === ConnectionState.Connected && !primary
	const [presenterOffline, setPresenterOffline] = useState(false)
	useEffect(() => {
		if (!waitingForPresenter) {
			setPresenterOffline(false)
			return
		}
		const timer = setTimeout(() => setPresenterOffline(true), PRESENTER_OFFLINE_TIMEOUT_MS)
		return () => clearTimeout(timer)
	}, [waitingForPresenter])

	if (!primary) {
		return (
			<PresenterScreenEmpty>
				{presenterOffline
					? t('viewer.screen.presenterOffline')
					: t('sessions.join.video.noPublishers')}
			</PresenterScreenEmpty>
		)
	}

	// Shared canonical renderer — same full-bleed/centered framing as the
	// live-sessions pane. The room page's outer container still owns the zoom
	// transform; `filterStyle` carries the grayscale/invert a11y controls.
	return <PresenterScreenTrack trackRef={primary} filterStyle={filterStyle} />
}
